import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CollapsibleFilter.css';

/**
 * Compact filter control: shows only the currently-selected option as a chip.
 * Clicking the chip opens a popover (rendered through a portal so it isn't
 * clipped by `overflow: hidden` ancestors) listing every option. Selecting an
 * option calls `onChange(value)` and closes the popover; the user may re-pick
 * the current value to keep it.
 *
 * @param {object} props
 * @param {string} props.label - Short uppercase label shown before the value (e.g. "Status").
 * @param {string} props.value - Currently-selected option value.
 * @param {{ value: string, label: string, disabled?: boolean }[]} props.options
 * @param {(value: string) => void} props.onChange
 * @param {boolean} [props.disabled] - Render as a static, non-interactive chip.
 * @param {string} [props.ariaLabel] - Accessible name for the control group.
 */
export default function CollapsibleFilter({
  label,
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const chipRef = useRef(null);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });

  const selected = options.find((o) => o.value === value) || options[0] || null;
  const selectedLabel = selected ? selected.label : '';

  // A control with a single (or no) choice is just a static chip.
  const interactive = !disabled && options.length > 1;

  const updatePosition = useCallback(() => {
    const el = chipRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + 6,
      left: r.left,
      minWidth: r.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (chipRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        chipRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSelect = useCallback(
    (next) => {
      setOpen(false);
      onChange?.(next);
    },
    [onChange],
  );

  return (
    <div className="collapsible-filter" role="group" aria-label={ariaLabel || label}>
      {label ? <span className="collapsible-filter-label">{label}</span> : null}
      <button
        ref={chipRef}
        type="button"
        className={`lg-toggle-btn active collapsible-filter-chip${interactive ? '' : ' collapsible-filter-chip--static'}`}
        onClick={interactive ? () => setOpen((v) => !v) : undefined}
        disabled={!interactive}
        aria-haspopup={interactive ? 'listbox' : undefined}
        aria-expanded={interactive ? open : undefined}
      >
        <span className="collapsible-filter-value">{selectedLabel}</span>
        {interactive ? <span className="collapsible-filter-caret" aria-hidden="true">▾</span> : null}
      </button>

      {interactive && open
        ? createPortal(
            <div
              ref={popoverRef}
              className="collapsible-filter-popover"
              role="listbox"
              aria-label={ariaLabel || label}
              style={{ top: pos.top, left: pos.left, minWidth: pos.minWidth }}
            >
              {options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  className={`collapsible-filter-option${o.value === value ? ' collapsible-filter-option--selected' : ''}`}
                  disabled={o.disabled}
                  onClick={() => handleSelect(o.value)}
                >
                  <span className="collapsible-filter-option-check" aria-hidden="true">
                    {o.value === value ? '✓' : ''}
                  </span>
                  {o.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
